/**
 * BOQ (Bill of Quantities) Calculation Service
 * Provides accurate component pricing and quantities for solar PV systems
 * Based on real Saudi market data (2024/2025)
 */

import type { CablePricingTable, CablePriceRow } from './cablePricingService';

export interface BOQItem {
  category: string;
  subcategory?: string;
  description: string;
  specification: string;
  quantity: number;
  unit: string;
  unitPrice: number; // SAR
  totalPrice: number; // SAR
  laborHours?: number;
  laborCost?: number; // SAR
}

export interface BOQSummary {
  items: BOQItem[];
  subtotals: {
    modules: number;
    inverters: number;
    mounting: number;
    electrical: number;
    monitoring: number;
    civil: number;
    labor: number;
  };
  totalMaterialCost: number;
  totalLaborCost: number;
  subtotal: number;
  vat: number; // 15%
  grandTotal: number;
}

export interface SelectedEquipmentModule {
  manufacturer: string;
  model: string;
  power_stc: number; // W
  efficiency?: number; // %
  warranty_years?: number;
  cell_type?: string;
  bifacial?: boolean;
  bifaciality_factor?: number | null;
  price_usd: number; // USD per module
}

export interface SelectedEquipmentInverter {
  manufacturer: string;
  model: string;
  power_ac_nominal: number; // kW
  phases?: number;
  inverter_type?: string;
  efficiency_max?: number; // %
  efficiency_euro?: number; // %
  warranty_years?: number;
  price_usd: number; // USD per inverter
}

export interface PricingOverrides {
  usdToSar?: number;
  vatRate?: number; // 0-1
  laborRateSarPerHour?: number;
}

export interface SystemConfiguration {
  systemSize: number; // kW
  panelCount: number;
  modulePower: number; // W
  inverterSize: number; // kW
  dcAcRatio?: number; // default 1.3
  mountingType:
    | 'ground-fixed'
    | 'roof-fixed'
    | 'single-axis'
    | 'single-axis-backtracking'
    | 'dual-axis'
    | 'carport'
    | 'carport-mounted';
  userType: 'residential' | 'commercial' | 'industrial' | 'agricultural';
  location: {
    latitude: number;
    longitude: number;
  };
  selectedModule?: SelectedEquipmentModule;
  selectedInverter?: SelectedEquipmentInverter;
  pricing?: PricingOverrides;

  /**
   * Optional IEC-derived cable sizing results.
   * If omitted, BOQ falls back to the existing heuristic sizing/length logic.
   */
  cables?: {
    ac?: {
      lengthM: number;
      sizeMm2: number;
      phase: 1 | 3;
      currentA?: number;
    };
    dc?: {
      lengthM: number;
      sizeMm2: number;
      currentA?: number;
      voltageV?: number;
    };
  };

  /**
   * Optional cable price table (usually fetched from Supabase `bos_components`).
   * If missing/unavailable, BOQ falls back to the legacy hardcoded cable pricing.
   */
  cablePricing?: CablePricingTable;

  /**
   * Optional mounting pricing (usually fetched from Supabase `bos_components`).
   * If missing/unavailable, BOQ falls back to the legacy hardcoded mounting pricing.
   */
  mountingPricing?: {
    roofFixed?: {
      unitPriceUsdPerKw: number;
      laborHoursPerKw?: number;
      laborRateUsdPerHour?: number;
      sourceComponentName?: string;
    };
  };
}

// Saudi Market Pricing (2024/2025) - All in SAR
const USD_TO_SAR = 3.75;
const VAT_RATE = 0.15; // 15%
const LABOR_RATE_SAR = 25 * USD_TO_SAR; // 93.75 SAR/hour

// Module Pricing (Average Saudi Market Rates)
const MODULE_PRICING = {
  residential: 0.52 * USD_TO_SAR, // SAR per Watt
  commercial: 0.48 * USD_TO_SAR,
  industrial: 0.45 * USD_TO_SAR,
  agricultural: 0.47 * USD_TO_SAR,
};

// Inverter Pricing (Average Saudi Market Rates)
const INVERTER_PRICING = {
  '0-10kW': 0.18 * USD_TO_SAR, // SAR per Watt
  '10-50kW': 0.15 * USD_TO_SAR,
  '50-100kW': 0.13 * USD_TO_SAR,
  '100kW+': 0.11 * USD_TO_SAR,
};

// Mounting Structure Pricing
const MOUNTING_PRICING = {
  'ground-fixed': 85 * USD_TO_SAR, // SAR per kW
  'roof-fixed': 70 * USD_TO_SAR,
  'single-axis': 180 * USD_TO_SAR,
  'carport': 220 * USD_TO_SAR,
};

type MountingPricingKey = keyof typeof MOUNTING_PRICING;

function normalizeMountingTypeForPricing(mountingType: unknown): MountingPricingKey {
  const mt = String(mountingType || '');
  switch (mt) {
    case 'ground-fixed':
    case 'roof-fixed':
    case 'single-axis':
    case 'carport':
      return mt;
    case 'carport-mounted':
      return 'carport';
    case 'single-axis-backtracking':
    case 'dual-axis':
      return 'single-axis';
    default:
      return 'roof-fixed';
  }
}

// DC Cable Pricing (SAR per meter)
const DC_CABLE_PRICING = {
  '4mm2': 1.2 * USD_TO_SAR,
  '6mm2': 1.8 * USD_TO_SAR,
  '10mm2': 2.8 * USD_TO_SAR,
};

// AC Cable Pricing (SAR per meter)
const AC_CABLE_PRICING = {
  '4x10mm2': 3.5 * USD_TO_SAR,
  '4x16mm2': 5.2 * USD_TO_SAR,
  '4x25mm2': 7.8 * USD_TO_SAR,
};

const PRICED_DC_SIZES_MM2 = [4, 6, 10] as const;
const PRICED_AC_SIZES_MM2 = [10, 16, 25] as const; // priced as 4-core

function toBestBySize(rows: CablePriceRow[] | undefined): Map<number, CablePriceRow> {
  const map = new Map<number, CablePriceRow>();
  if (!rows) return map;
  for (const r of rows) {
    const size = r.sizeMm2;
    if (!Number.isFinite(size) || size <= 0) continue;
    const existing = map.get(size);
    if (!existing || r.unitPriceUsdPerM < existing.unitPriceUsdPerM) {
      map.set(size, r);
    }
  }
  return map;
}

function sortedSizesFromMap(map: Map<number, unknown>): number[] {
  return [...map.keys()].sort((a, b) => a - b);
}

function roundUpToNext(value: number, options: readonly number[]): number {
  const sorted = [...options].sort((a, b) => a - b);
  for (const o of sorted) {
    if (value <= o) return o;
  }
  return sorted[sorted.length - 1];
}

function formatMm2(sizeMm2: number): string {
  // Keep formatting consistent with existing pricing keys (mm2)
  return `${Number.isInteger(sizeMm2) ? sizeMm2 : sizeMm2}mm2`;
}

function resolveDcCablePricing(sizeMm2: number): {
  pricedSizeMm2: number;
  unitPriceSarPerM: number;
  pricingCapped: boolean;
  source: 'db' | 'fallback';
  sourceComponentName?: string;
  laborHoursPerM: number;
  laborRateSarPerHour: number;
} {
  // This signature is kept, but actual resolution happens via the wrapper below.
  // (This function is overwritten by the wrapper inside calculateBOQ).
  const maxPriced = Math.max(...PRICED_DC_SIZES_MM2);
  const pricingCapped = sizeMm2 > maxPriced;
  const pricedSizeMm2 = pricingCapped ? maxPriced : roundUpToNext(sizeMm2, PRICED_DC_SIZES_MM2);
  const key = `${formatMm2(pricedSizeMm2)}` as keyof typeof DC_CABLE_PRICING;
  const unitPriceSarPerM = DC_CABLE_PRICING[key] ?? 0;
  return {
    pricedSizeMm2,
    unitPriceSarPerM,
    pricingCapped,
    source: 'fallback',
    laborHoursPerM: 0.02,
    laborRateSarPerHour: LABOR_RATE_SAR,
  };
}

function resolveAcCablePricing(args: {
  phase: 1 | 3;
  sizeMm2: number;
}): {
  pricedSizeMm2: number;
  pricedCoreCount: 2 | 4;
  unitPriceSarPerM: number;
  pricingBasis: string;
  pricingCapped: boolean;
  source: 'db' | 'fallback';
  sourceComponentName?: string;
  laborHoursPerM: number;
  laborRateSarPerHour: number;
} {
  // This signature is kept, but actual resolution happens via the wrapper below.
  const { phase, sizeMm2 } = args;
  const maxPriced = Math.max(...PRICED_AC_SIZES_MM2);
  const pricingCapped = sizeMm2 > maxPriced;
  const pricedSizeMm2 = pricingCapped ? maxPriced : roundUpToNext(sizeMm2, PRICED_AC_SIZES_MM2);
  const key = `4x${formatMm2(pricedSizeMm2)}` as keyof typeof AC_CABLE_PRICING;
  const price4Core = AC_CABLE_PRICING[key] ?? 0;

  if (phase === 3) {
    return {
      pricedSizeMm2,
      pricedCoreCount: 4,
      unitPriceSarPerM: price4Core,
      pricingBasis: `${key}`,
      pricingCapped,
      source: 'fallback',
      laborHoursPerM: 0.03,
      laborRateSarPerHour: LABOR_RATE_SAR,
    };
  }

  // Single phase: price using a 2-core equivalent derived from the 4-core priced rate.
  return {
    pricedSizeMm2,
    pricedCoreCount: 2,
    unitPriceSarPerM: price4Core * 0.5,
    pricingBasis: `0.5 × (${key})`,
    pricingCapped,
    source: 'fallback',
    laborHoursPerM: 0.03,
    laborRateSarPerHour: LABOR_RATE_SAR,
  };
}

/**
 * Calculate comprehensive BOQ for a solar PV system
 */
export function calculateBOQ(config: SystemConfiguration): BOQSummary {
  const items: BOQItem[] = [];
  const usdToSar = config.pricing?.usdToSar ?? USD_TO_SAR;
  const vatRate = config.pricing?.vatRate ?? VAT_RATE;
  const laborRateSar = config.pricing?.laborRateSarPerHour ?? LABOR_RATE_SAR;
  const dcAcRatio = config.dcAcRatio ?? 1.3;

  // Build DB-backed cable pricing lookups (if provided)
  const dcDbBySize = toBestBySize(config.cablePricing?.dc);
  const ac3DbBySize = toBestBySize(config.cablePricing?.ac3);
  const ac1DbBySize = toBestBySize(config.cablePricing?.ac1);

  const dcDbSizes = sortedSizesFromMap(dcDbBySize);
  const ac3DbSizes = sortedSizesFromMap(ac3DbBySize);
  const ac1DbSizes = sortedSizesFromMap(ac1DbBySize);

  const resolveDc = (requiredSizeMm2: number) => {
    if (dcDbSizes.length > 0) {
      const max = dcDbSizes[dcDbSizes.length - 1];
      const pricingCapped = requiredSizeMm2 > max;
      const pricedSizeMm2 = pricingCapped ? max : roundUpToNext(requiredSizeMm2, dcDbSizes);
      const row = dcDbBySize.get(pricedSizeMm2);
      const unitPriceSarPerM = row ? row.unitPriceUsdPerM * usdToSar : 0;
      const laborHoursPerM = row?.laborHoursPerM ?? 0.02;
      const laborRateSarPerHour = row?.laborRateUsdPerHour ? row.laborRateUsdPerHour * usdToSar : laborRateSar;
      return {
        pricedSizeMm2,
        unitPriceSarPerM,
        pricingCapped,
        source: 'db' as const,
        sourceComponentName: row?.componentName,
        laborHoursPerM,
        laborRateSarPerHour,
      };
    }
    return resolveDcCablePricing(requiredSizeMm2);
  };

  const resolveAc = (args: { phase: 1 | 3; requiredSizeMm2: number }) => {
    const { phase, requiredSizeMm2 } = args;

    // Prefer explicit DB pricing for 1φ if present; otherwise derive from 3φ table (legacy behavior).
    if (phase === 1 && ac1DbSizes.length > 0) {
      const max = ac1DbSizes[ac1DbSizes.length - 1];
      const pricingCapped = requiredSizeMm2 > max;
      const pricedSizeMm2 = pricingCapped ? max : roundUpToNext(requiredSizeMm2, ac1DbSizes);
      const row = ac1DbBySize.get(pricedSizeMm2);
      const unitPriceSarPerM = row ? row.unitPriceUsdPerM * usdToSar : 0;
      const laborHoursPerM = row?.laborHoursPerM ?? 0.03;
      const laborRateSarPerHour = row?.laborRateUsdPerHour ? row.laborRateUsdPerHour * usdToSar : laborRateSar;
      return {
        pricedSizeMm2,
        pricedCoreCount: 2 as const, // pricing table may be 3-core; we don’t assume here
        unitPriceSarPerM,
        pricingBasis: row?.componentName ? `DB: ${row.componentName}` : 'DB',
        pricingCapped,
        source: 'db' as const,
        sourceComponentName: row?.componentName,
        laborHoursPerM,
        laborRateSarPerHour,
      };
    }

    if (ac3DbSizes.length > 0) {
      const max = ac3DbSizes[ac3DbSizes.length - 1];
      const pricingCapped = requiredSizeMm2 > max;
      const pricedSizeMm2 = pricingCapped ? max : roundUpToNext(requiredSizeMm2, ac3DbSizes);
      const row = ac3DbBySize.get(pricedSizeMm2);
      const basePriceSarPerM = row ? row.unitPriceUsdPerM * usdToSar : 0;
      const laborHoursPerM = row?.laborHoursPerM ?? 0.03;
      const laborRateSarPerHour = row?.laborRateUsdPerHour ? row.laborRateUsdPerHour * usdToSar : laborRateSar;

      if (phase === 3) {
        return {
          pricedSizeMm2,
          pricedCoreCount: 4 as const,
          unitPriceSarPerM: basePriceSarPerM,
          pricingBasis: row?.componentName ? `DB: ${row.componentName}` : 'DB',
          pricingCapped,
          source: 'db' as const,
          sourceComponentName: row?.componentName,
          laborHoursPerM,
          laborRateSarPerHour,
        };
      }

      // Single phase fallback: derive price from 3-phase 4-core price (legacy behavior).
      return {
        pricedSizeMm2,
        pricedCoreCount: 2 as const,
        unitPriceSarPerM: basePriceSarPerM * 0.5,
        pricingBasis: `0.5 × (DB: ${row?.componentName ?? `4x${formatMm2(pricedSizeMm2)}`})`,
        pricingCapped,
        source: 'db' as const,
        sourceComponentName: row?.componentName,
        laborHoursPerM,
        laborRateSarPerHour,
      };
    }

    // Fallback to legacy hardcoded list
    return resolveAcCablePricing({ phase, sizeMm2: requiredSizeMm2 });
  };
  
  // 1. Solar Modules
  const hasSelectedModule = !!config.selectedModule;
  const moduleUnitPriceSar = hasSelectedModule
    ? (config.selectedModule!.price_usd * usdToSar)
    : (config.modulePower * MODULE_PRICING[config.userType]); // fallback SAR/module
  const moduleTotalPrice = config.panelCount * moduleUnitPriceSar;
  
  items.push({
    category: 'Solar Modules',
    description: hasSelectedModule
      ? `${config.selectedModule!.manufacturer} ${config.selectedModule!.model} (${config.selectedModule!.power_stc}W)`
      : `Solar PV Modules ${config.modulePower}W`,
    specification: hasSelectedModule
      ? [
          `Power (STC): ${config.selectedModule!.power_stc}W`,
          config.selectedModule!.efficiency != null ? `Efficiency: ${config.selectedModule!.efficiency}%` : null,
          config.selectedModule!.cell_type ? `Cell type: ${config.selectedModule!.cell_type}` : null,
          config.selectedModule!.bifacial ? `Bifacial: Yes${config.selectedModule!.bifaciality_factor != null ? ` (${config.selectedModule!.bifaciality_factor}%)` : ''}` : 'Bifacial: No',
          config.selectedModule!.warranty_years != null ? `Warranty: ${config.selectedModule!.warranty_years} years` : null,
        ].filter(Boolean).join(' | ')
      : `Mono-PERC/TOPCon, 25-year warranty, ${config.modulePower}W per module`,
    quantity: config.panelCount,
    unit: 'pieces',
    unitPrice: moduleUnitPriceSar,
    totalPrice: moduleTotalPrice,
    laborHours: config.panelCount * 0.3,
    laborCost: config.panelCount * 0.3 * laborRateSar,
  });
  
  // 2. Inverters
  const hasSelectedInverter = !!config.selectedInverter;
  const requiredAcKw = config.systemSize / dcAcRatio;
  const inverterCount = Math.max(1, Math.ceil(requiredAcKw / config.inverterSize));
  const inverterUnitPriceSar = hasSelectedInverter
    ? (config.selectedInverter!.price_usd * usdToSar)
    : (config.inverterSize * 1000 * getInverterPricing(config.inverterSize));
  const inverterTotalPrice = inverterCount * inverterUnitPriceSar;
  
  items.push({
    category: 'Inverters',
    description: hasSelectedInverter
      ? `${config.selectedInverter!.manufacturer} ${config.selectedInverter!.model} (${config.selectedInverter!.power_ac_nominal}kW)`
      : `Solar Inverter ${config.inverterSize}kW`,
    specification: hasSelectedInverter
      ? [
          `AC nominal: ${config.selectedInverter!.power_ac_nominal}kW`,
          config.selectedInverter!.inverter_type ? `Type: ${config.selectedInverter!.inverter_type}` : null,
          config.selectedInverter!.phases != null ? `Phases: ${config.selectedInverter!.phases}` : null,
          config.selectedInverter!.efficiency_euro != null ? `Efficiency (EU): ${config.selectedInverter!.efficiency_euro}%` : null,
          config.selectedInverter!.efficiency_max != null ? `Efficiency (max): ${config.selectedInverter!.efficiency_max}%` : null,
          config.selectedInverter!.warranty_years != null ? `Warranty: ${config.selectedInverter!.warranty_years} years` : null,
        ].filter(Boolean).join(' | ')
      : `String inverter, ${config.inverterSize}kW AC output, 10-year warranty, 98%+ efficiency`,
    quantity: inverterCount,
    unit: 'pieces',
    unitPrice: inverterUnitPriceSar,
    totalPrice: inverterTotalPrice,
    laborHours: inverterCount * 2,
    laborCost: inverterCount * 2 * laborRateSar,
  });
  
  // 3. Mounting Structure
  const normalizedMountingType = normalizeMountingTypeForPricing(config.mountingType);

  const roofFixedDb = config.mountingPricing?.roofFixed;
  const hasRoofFixedDb = normalizedMountingType === 'roof-fixed' && roofFixedDb?.unitPriceUsdPerKw != null;

  const mountingPricePerKW =
    hasRoofFixedDb && roofFixedDb
      ? roofFixedDb.unitPriceUsdPerKw * usdToSar
      : MOUNTING_PRICING[normalizedMountingType];
  const mountingTotalPrice = config.systemSize * mountingPricePerKW;

  const mountingLaborHoursPerKw =
    hasRoofFixedDb && roofFixedDb?.laborHoursPerKw != null
      ? roofFixedDb.laborHoursPerKw
      : config.mountingType === 'ground-fixed'
        ? 0.8
        : 0.6;
  const mountingLaborRateSarPerHour =
    hasRoofFixedDb && roofFixedDb?.laborRateUsdPerHour != null
      ? roofFixedDb.laborRateUsdPerHour * usdToSar
      : laborRateSar;
  const mountingLaborHours = config.systemSize * mountingLaborHoursPerKw;
  
  items.push({
    category: 'Mounting Structure',
    subcategory: config.mountingType,
    description: `${config.mountingType.replace('-', ' ')} mounting system`,
    specification: 'Aluminum/steel structure, wind load 150km/h, includes rails and clamps',
    quantity: config.systemSize,
    unit: 'kW',
    unitPrice: mountingPricePerKW,
    totalPrice: mountingTotalPrice,
    laborHours: mountingLaborHours,
    laborCost: mountingLaborHours * mountingLaborRateSarPerHour,
  });
  
  // 4. DC Cables
  const dcSizedSizeMm2 =
    config.cables?.dc?.sizeMm2 != null && config.cables.dc.sizeMm2 > 0 ? config.cables.dc.sizeMm2 : undefined;
  const dcCableLength =
    config.cables?.dc?.lengthM != null && config.cables.dc.lengthM > 0
      ? config.cables.dc.lengthM
      : calculateDCCableLength(config.panelCount, config.mountingType);

  const dcFallbackSizeMm2 = config.systemSize > 50 ? 10 : config.systemSize > 20 ? 6 : 4;
  const dcEffectiveSizeMm2 = dcSizedSizeMm2 ?? dcFallbackSizeMm2;
  const dcPricing = resolveDc(dcEffectiveSizeMm2);
  const dcCablePricePerMeter = dcPricing.unitPriceSarPerM;
  const dcCableTotalPrice = dcCableLength * dcCablePricePerMeter;
  
  items.push({
    category: 'DC Cables',
    description: `DC Solar Cable (Cu) ${formatMm2(dcEffectiveSizeMm2)}`,
    specification: [
      '1500V rated, UV resistant, TUV certified',
      config.cables?.dc ? 'IEC 60364-5-52 sizing (copper, multicore)' : null,
      config.cables?.dc?.currentA != null ? `Design current: ${Math.round(config.cables.dc.currentA)}A` : null,
      config.cables?.dc?.voltageV != null ? `DC reference voltage: ${Math.round(config.cables.dc.voltageV)}V` : null,
      dcPricing.pricedSizeMm2 !== dcEffectiveSizeMm2
        ? dcPricing.pricingCapped
          ? `Priced as ${formatMm2(dcPricing.pricedSizeMm2)} (max priced size; add pricing for larger cables)`
          : `Priced as ${formatMm2(dcPricing.pricedSizeMm2)} (rounded up to nearest priced size)`
        : null,
      dcPricing.source === 'db' ? `Pricing source: Supabase (bos_components)` : 'Pricing source: fallback',
      dcPricing.sourceComponentName ? `DB item: ${dcPricing.sourceComponentName}` : null,
    ]
      .filter(Boolean)
      .join(' | '),
    quantity: dcCableLength,
    unit: 'meters',
    unitPrice: dcCablePricePerMeter,
    totalPrice: dcCableTotalPrice,
    laborHours: dcCableLength * dcPricing.laborHoursPerM,
    laborCost: dcCableLength * dcPricing.laborHoursPerM * dcPricing.laborRateSarPerHour,
  });
  
  // 5. AC Cables
  const acSizedSizeMm2 =
    config.cables?.ac?.sizeMm2 != null && config.cables.ac.sizeMm2 > 0 ? config.cables.ac.sizeMm2 : undefined;
  const acPhase = config.cables?.ac?.phase;
  const acCableLength =
    config.cables?.ac?.lengthM != null && config.cables.ac.lengthM > 0
      ? config.cables.ac.lengthM
      : calculateACCableLength(config.systemSize);

  // Fallback keeps existing behavior (3-phase assumption) if no sized cable is provided.
  const acFallbackSizeMm2 = config.systemSize > 50 ? 25 : config.systemSize > 20 ? 16 : 10;
  const acEffectiveSizeMm2 = acSizedSizeMm2 ?? acFallbackSizeMm2;
  const acEffectivePhase: 1 | 3 = acPhase ?? 3;
  const acPricing = resolveAc({ phase: acEffectivePhase, requiredSizeMm2: acEffectiveSizeMm2 });
  const acCablePricePerMeter = acPricing.unitPriceSarPerM;
  const acCableTotalPrice = acCableLength * acCablePricePerMeter;
  
  items.push({
    category: 'AC Cables',
    description: `AC Cable (Cu) ${formatMm2(acEffectiveSizeMm2)} (${acEffectivePhase === 1 ? '1φ' : '3φ'})`,
    specification: [
      'From inverter to AC panel',
      acEffectivePhase === 1 ? '230V nominal' : '415V nominal',
      config.cables?.ac ? 'IEC 60364-5-52 sizing (copper, multicore)' : null,
      config.cables?.ac?.currentA != null ? `Design current: ${Math.round(config.cables.ac.currentA)}A` : null,
      `Priced as ${acPricing.pricedCoreCount}x${formatMm2(acPricing.pricedSizeMm2)} (${acPricing.pricingBasis})`,
      acPricing.pricedSizeMm2 !== acEffectiveSizeMm2
        ? acPricing.pricingCapped
          ? `Sized ${formatMm2(acEffectiveSizeMm2)} → priced ${formatMm2(acPricing.pricedSizeMm2)} (max priced size; may be underpriced)`
          : `Sized ${formatMm2(acEffectiveSizeMm2)} → priced ${formatMm2(acPricing.pricedSizeMm2)} (rounded up)`
        : null,
      acPricing.source === 'db' ? 'Pricing source: Supabase (bos_components)' : 'Pricing source: fallback',
      acPricing.sourceComponentName ? `DB item: ${acPricing.sourceComponentName}` : null,
    ]
      .filter(Boolean)
      .join(' | '),
    quantity: acCableLength,
    unit: 'meters',
    unitPrice: acCablePricePerMeter,
    totalPrice: acCableTotalPrice,
    laborHours: acCableLength * acPricing.laborHoursPerM,
    laborCost: acCableLength * acPricing.laborHoursPerM * acPricing.laborRateSarPerHour,
  });
  
  // 6. Earthing System
  // - Rods: residential = 1, others = 3
  // - Earthing cable: total installed cost = 50% of AC cable installed cost (materials + labor)
  const earthingRods = config.userType === 'residential' ? 1 : 3;
  const earthingRodPrice = 35 * usdToSar;
  const earthingCableLength = config.panelCount * 2; // keep legacy length model for reporting

  const acCableInstalledMaterial = acCableTotalPrice;
  const acCableInstalledLabor = acCableLength * acPricing.laborHoursPerM * acPricing.laborRateSarPerHour;
  const earthingCableMaterialTotal = 0.5 * acCableInstalledMaterial;
  const earthingCableLaborTotal = 0.5 * acCableInstalledLabor;
  const earthingCableUnitPrice =
    earthingCableLength > 0 ? earthingCableMaterialTotal / earthingCableLength : 0;

  // Rods item
  items.push({
    category: 'Earthing System',
    description: 'Earthing rods',
    specification: '1.5m copper-clad rods',
    quantity: earthingRods,
    unit: 'pieces',
    unitPrice: earthingRodPrice,
    totalPrice: earthingRods * earthingRodPrice,
    laborHours: earthingRods * 0.8,
    laborCost: earthingRods * 0.8 * laborRateSar,
  });

  // Cable item
  items.push({
    category: 'Earthing Cable',
    description: 'Earthing cable',
    specification: 'Cost rule: 50% of AC cable installed cost',
    quantity: earthingCableLength,
    unit: 'meters',
    unitPrice: earthingCableUnitPrice,
    totalPrice: earthingCableMaterialTotal,
    laborHours: earthingCableLaborTotal / laborRateSar,
    laborCost: earthingCableLaborTotal,
  });

  // 7. Monitoring System
  const monitoringPrice = config.systemSize > 50 ? 450 * USD_TO_SAR : 250 * USD_TO_SAR;
  
  items.push({
    category: 'Monitoring System',
    description: config.systemSize > 50 ? 'Advanced Monitoring System' : 'Basic Monitoring System',
    specification: config.systemSize > 50 ? '4G connectivity, analytics, sensors' : 'WiFi connectivity, production monitoring',
    quantity: 1,
    unit: 'system',
    unitPrice: monitoringPrice,
    totalPrice: monitoringPrice,
    laborHours: 1.5,
    laborCost: 1.5 * laborRateSar,
  });
  
  // 10. Civil Works (for ground-mounted systems)
  if (config.mountingType === 'ground-fixed' || config.mountingType === 'single-axis') {
    const foundationCostPerModule = 15 * USD_TO_SAR;
    const foundationTotal = config.panelCount * foundationCostPerModule;
    
    items.push({
      category: 'Civil Works',
      description: 'Concrete foundations',
      specification: 'Concrete pier foundations, 1m depth',
      quantity: config.panelCount,
      unit: 'pieces',
      unitPrice: foundationCostPerModule,
      totalPrice: foundationTotal,
      laborHours: config.panelCount * 0.3,
      laborCost: config.panelCount * 0.3 * laborRateSar,
    });
    
    // Cable trenching
    const trenchingLength = Math.ceil(config.systemSize * 5); // 5m per kW average
    const trenchingPricePerMeter = 8 * USD_TO_SAR;
    
    items.push({
      category: 'Civil Works',
      description: 'Cable trenching',
      specification: '0.8m depth, 0.3m width, backfill included',
      quantity: trenchingLength,
      unit: 'meters',
      unitPrice: trenchingPricePerMeter,
      totalPrice: trenchingLength * trenchingPricePerMeter,
      laborHours: trenchingLength * 0.2,
      laborCost: trenchingLength * 0.2 * laborRateSar,
    });
  }
  
  // 11. Percent adders (computed from BOQ pre-VAT subtotal, without circularity)
  // Requirements:
  // - Connectors/junction boxes/labels/cable management = 1% of BOQ
  // - Engineering design & drawings = 2% of BOQ
  // - Project management & supervision = 2% of BOQ
  //
  // We interpret “BOQ” here as the pre-VAT subtotal INCLUDING these percent adders.
  // Solve: total = base / (1 - p), where p is sum of the fee percentages.
  const baseMaterialCost = items.reduce((sum, item) => sum + item.totalPrice, 0);
  const baseLaborCost = items.reduce((sum, item) => sum + (item.laborCost || 0), 0);
  const baseSubtotal = baseMaterialCost + baseLaborCost;

  const miscPct = 0.01;
  const engineeringPct = 0.02;
  const pmPct = 0.02;
  const p = miscPct + engineeringPct + pmPct;

  const subtotalWithPercentFees = p < 1 ? baseSubtotal / (1 - p) : baseSubtotal;
  const miscFee = miscPct * subtotalWithPercentFees;
  const engineeringFee = engineeringPct * subtotalWithPercentFees;
  const pmFee = pmPct * subtotalWithPercentFees;

  items.push({
    category: 'Miscellaneous',
    description: 'Connectors, junction boxes, labels, cable management',
    specification: 'Percent adder: 1% of BOQ (pre-VAT subtotal)',
    quantity: 1,
    unit: 'lot',
    unitPrice: miscFee,
    totalPrice: miscFee,
  });

  items.push({
    category: 'Engineering & Design',
    description: 'Engineering design and drawings',
    specification: 'Percent adder: 2% of BOQ (pre-VAT subtotal)',
    quantity: 1,
    unit: 'lot',
    unitPrice: engineeringFee,
    totalPrice: engineeringFee,
  });

  items.push({
    category: 'Project Management',
    description: 'Project management and supervision',
    specification: 'Percent adder: 2% of BOQ (pre-VAT subtotal)',
    quantity: 1,
    unit: 'lot',
    unitPrice: pmFee,
    totalPrice: pmFee,
  });
  
  // Calculate subtotals
  const subtotals = {
    modules: items.filter(i => i.category === 'Solar Modules').reduce((sum, i) => sum + i.totalPrice, 0),
    inverters: items.filter(i => i.category === 'Inverters').reduce((sum, i) => sum + i.totalPrice, 0),
    mounting: items.filter(i => i.category === 'Mounting Structure').reduce((sum, i) => sum + i.totalPrice, 0),
    electrical: items
      .filter(i => ['DC Cables', 'AC Cables', 'Earthing Cable', 'Earthing System'].includes(i.category))
      .reduce((sum, i) => sum + i.totalPrice, 0),
    monitoring: items.filter(i => i.category === 'Monitoring System').reduce((sum, i) => sum + i.totalPrice, 0),
    civil: items.filter(i => i.category === 'Civil Works').reduce((sum, i) => sum + i.totalPrice, 0),
    labor: items.reduce((sum, i) => sum + (i.laborCost || 0), 0),
  };
  
  const totalMaterialCostFinal = items.reduce((sum, item) => sum + item.totalPrice, 0);
  const totalLaborCost = items.reduce((sum, item) => sum + (item.laborCost || 0), 0);
  const subtotal = totalMaterialCostFinal + totalLaborCost;
  const vat = subtotal * vatRate;
  const grandTotal = subtotal + vat;
  
  return {
    items,
    subtotals,
    totalMaterialCost: totalMaterialCostFinal,
    totalLaborCost,
    subtotal,
    vat,
    grandTotal,
  };
}

/**
 * Helper Functions
 */

function getInverterPricing(inverterSize: number): number {
  if (inverterSize <= 10) return INVERTER_PRICING['0-10kW'];
  if (inverterSize <= 50) return INVERTER_PRICING['10-50kW'];
  if (inverterSize <= 100) return INVERTER_PRICING['50-100kW'];
  return INVERTER_PRICING['100kW+'];
}

function calculateDCCableLength(panelCount: number, mountingType: string): number {
  const mt = normalizeMountingTypeForPricing(mountingType);
  // Average cable length per module based on mounting type
  const avgLengthPerModule = {
    'ground-fixed': 8, // meters
    'roof-fixed': 6,
    'single-axis': 10,
    'carport': 7,
  };
  
  return panelCount * (avgLengthPerModule[mt] || 7);
}

function calculateACCableLength(systemSize: number): number {
  // Average AC cable run: 30m for small systems, 50m for medium, 100m for large
  if (systemSize <= 20) return 30;
  if (systemSize <= 100) return 50;
  return 100;
}
